import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Navigate } from '@sorare/routing';
import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_HOME,
  ANY_SPORT_SERIES,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import {
  WalletLandingWithBranding,
  walletLandingLoader,
} from '@sorare/gameplay/src/components/landing/WalletLanding/index.tsx';

import { FAQ } from '#src/components/landing/FAQ/index.tsx';

const messages = defineMessages({
  metadatasTitle: {
    defaultMessage:
      'Play fantasy basketball with ownable NBA player cards - Sorare',
  },
  metadatasDescription: {
    defaultMessage:
      "Play Sorare's free fantasy basketball game. Collect, buy, sell & compete with ownable digital player cards from all 30 NBA teams to win cash, ETH, tickets, more",
  },
});

type Props = {
  isRoot?: boolean;
};

export const loader = walletLandingLoader('nba');

const NbaPage = ({ isRoot }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const track = useEvents();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: 'https://frontend-assets.sorare.com/meta/social-nba-2026.jpg',
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      track('View Homepage Disconnected', {
        landing_variant: 'NBA',
      });
    }
  }, [isLoggedIn, track]);

  if (isLoggedIn) {
    if (currentUser && !currentUser.anyUserSportProfile?.proUnlocked) {
      return (
        <Navigate
          to={generateSportContextPath(ANY_SPORT_SERIES)}
          replace
          keepSearchParams
        />
      );
    }
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_HOME)}
        replace
        keepSearchParams
      />
    );
  }

  return (
    <WalletLandingWithBranding
      withoutLoader={isRoot}
      variant="nba"
      leaderboards={[
        So5LeaderboardType.NBA_LIMITED_CHAMPION,
        So5LeaderboardType.NBA_UNIQUE_CHAMPION,
        So5LeaderboardType.NBA_RARE_CHAMPION,
      ]}
      showFAQ={!isRoot}
      faq={<FAQ />}
    />
  );
};

export default NbaPage;
